import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Shield, 
  ArrowLeft,
  ArrowRight,
  Check,
  Clock,
  FileText,
  Building2,
  Truck,
  Users,
  DollarSign,
  AlertTriangle,
  CheckCircle2,
  Car,
  UserCheck,
  Route,
  ShieldCheck,
  Scale,
  TrendingDown,
  Heart,
  Eye,
  Zap
} from 'lucide-react';
import { trackPageView } from '../hooks/useAnalytics';

// Coverage periods visualization data
const coveragePeriods = [
  {
    period: 'Period 1',
    title: 'App Off / Personal Use',
    description: 'Driver is using vehicle for personal purposes. Not logged into dispatch.',
    coverage: 'Your Personal Insurance',
    icon: Car,
    color: 'slate',
    bgColor: 'bg-slate-500/10',
    borderColor: 'border-slate-500/30',
    textColor: 'text-slate-400'
  },
  {
    period: 'Period 2',
    title: 'App On / Waiting for Trip',
    description: 'Driver is logged in and available, waiting for a trip assignment.',
    coverage: 'Your Commercial Insurance',
    icon: Clock,
    color: 'yellow',
    bgColor: 'bg-yellow-500/10',
    borderColor: 'border-yellow-500/30',
    textColor: 'text-yellow-400'
  },
  {
    period: 'Period 3',
    title: 'En Route to Pickup',
    description: 'Trip accepted. Driver is on the way to pick up the passenger.',
    coverage: 'Medex Group Insurance',
    icon: Route,
    color: 'cyan',
    bgColor: 'bg-neon-cyan/10',
    borderColor: 'border-neon-cyan/30',
    textColor: 'text-neon-cyan'
  },
  {
    period: 'Period 4',
    title: 'Passenger On Board',
    description: 'Passenger picked up. Active medical transport in progress until drop-off.',
    coverage: 'Medex Group Insurance',
    icon: UserCheck,
    color: 'green',
    bgColor: 'bg-neon-green/10',
    borderColor: 'border-neon-green/30',
    textColor: 'text-neon-green'
  }
];

// Onboarding fee tiers
const onboardingTiers = [
  { range: '1-4 vehicles', fee: '$500', popular: false },
  { range: '5-10 vehicles', fee: '$1,000', popular: true },
  { range: '15-25 vehicles', fee: '$2,500', popular: false },
  { range: '25+ vehicles', fee: 'Contact Us', popular: false }
];

// Requirements checklist
const enrollmentRequirements = [
  { item: '2 years of loss runs', description: 'Historical claims data from your current insurer' },
  { item: 'Company legal business name', description: 'As registered with your state' },
  { item: 'Years in business', description: 'Minimum operational history required' },
  { item: 'Business address', description: 'Physical location of your operations' },
  { item: 'Current insurance company', description: 'Your existing coverage provider' },
  { item: 'Current premium amount', description: 'What you\'re paying today' },
  { item: 'Fleet size and vehicle list', description: 'VINs, year, make, model for each vehicle' }
];

// Insurance minimums
const insuranceMinimums = [
  { 
    type: 'Commercial Auto Liability',
    requirement: '100/300/100 or 300 CSL',
    description: 'Bodily injury and property damage coverage'
  },
  {
    type: 'General Liability',
    requirement: '$1 Million',
    description: 'Or your state minimum requirement'
  }
];

export const InsuranceDetailsPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    trackPageView('/insurance/details', 'insurance_details');
  }, []);

  return (
    <div className="min-h-screen bg-medex-bg">
      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-neon-purple/10 via-transparent to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/insurance')}
            className="inline-flex items-center text-slate-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Insurance
          </button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl"
          >
            <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-neon-purple/10 border border-neon-purple/30 mb-6">
              <Shield className="w-4 h-4 text-neon-purple" />
              <span className="text-sm text-neon-purple font-medium">Medex Group Insurance Plan</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white mb-6">
              Revolutionary NEMT Insurance <span className="text-neon-cyan">Coverage</span>
            </h1>
            
            <p className="text-xl text-slate-400 mb-8 max-w-3xl">
              Medex is reshaping the NEMT industry with a groundbreaking insurance model. 
              Learn how our group plan provides $1 Million coverage while addressing industry 
              disparities and combating fraud.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How Coverage Works - Rideshare Model */}
      <section className="py-16 bg-medex-surface/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              How <span className="text-neon-cyan">Coverage</span> Works
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Similar to rideshare insurance models, Medex coverage activates based on your 
              trip status. Here's when each policy applies:
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coveragePeriods.map((period, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`glass-card p-6 ${period.borderColor} relative overflow-hidden`}
              >
                {(period.period === 'Period 3' || period.period === 'Period 4') && (
                  <div className="absolute top-0 right-0 bg-neon-green text-black text-xs font-bold px-2 py-1 rounded-bl">
                    MEDEX COVERED
                  </div>
                )}
                <div className="text-xs font-mono text-slate-500 uppercase tracking-wider mb-3">
                  {period.period}
                </div>
                <div className={`w-12 h-12 rounded-xl ${period.bgColor} flex items-center justify-center mb-4`}>
                  <period.icon className={`w-6 h-6 ${period.textColor}`} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{period.title}</h3>
                <p className="text-sm text-slate-400 mb-4">{period.description}</p>
                <div className={`text-sm font-medium ${period.textColor} flex items-center`}>
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  {period.coverage}
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-8 glass-card p-6 border-neon-cyan/30 bg-neon-cyan/5"
          >
            <div className="flex items-start space-x-4">
              <ShieldCheck className="w-6 h-6 text-neon-cyan flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-white mb-2">When You're Protected by Medex</h4>
                <p className="text-slate-300">
                  Once a trip is accepted and you're en route to pickup, through passenger transport 
                  until trip completion, you're covered under the Medex Group Insurance Plan at 
                  <span className="text-neon-green font-semibold"> $1 Million in coverage</span>. 
                  This is when it matters most - when you have a contractual obligation to the passenger.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Requirements to Join */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left - Requirements */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-heading font-bold text-white mb-6">
                Requirements to <span className="text-neon-purple">Join</span>
              </h2>
              
              <div className="glass-card p-6 mb-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Truck className="w-6 h-6 text-neon-purple" />
                  <h3 className="text-lg font-semibold text-white">Transportation Providers Only</h3>
                </div>
                <p className="text-slate-400 text-sm">
                  Medex Group Insurance is exclusively available to licensed NEMT transportation 
                  providers operating legitimate medical transport services.
                </p>
              </div>

              {/* Software Requirement */}
              <div className="glass-card p-6 mb-6 border-neon-cyan/30">
                <div className="flex items-center space-x-3 mb-4">
                  <Zap className="w-6 h-6 text-neon-cyan" />
                  <h3 className="text-lg font-semibold text-white">Approved NEMT Trip Software</h3>
                  <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full">REQUIRED</span>
                </div>
                <p className="text-slate-400 text-sm">
                  You must use an approved NEMT trip/dispatch software for accurate trip tracking. 
                  This is essential for activity-based billing and coverage verification. 
                  MedexOVA dispatch software or compatible third-party systems accepted.
                </p>
              </div>

              {/* Camera Integration */}
              <div className="glass-card p-6 mb-6 border-neon-green/20">
                <div className="flex items-center space-x-3 mb-4">
                  <Eye className="w-6 h-6 text-neon-green" />
                  <h3 className="text-lg font-semibold text-white">Camera Integration</h3>
                  <span className="text-xs bg-neon-green/20 text-neon-green px-2 py-0.5 rounded-full">RECOMMENDED</span>
                </div>
                <p className="text-slate-400 text-sm">
                  AI dash cam integration (NEMT Vision) is a plus for enhanced safety monitoring. 
                  <span className="text-yellow-400"> Some broker contracts will require camera systems</span> - 
                  having cameras already installed positions you for more opportunities.
                </p>
              </div>

              <div className="space-y-4">
                <h4 className="text-sm font-mono text-slate-500 uppercase tracking-wider">
                  Insurance Minimums Required
                </h4>
                {insuranceMinimums.map((item, index) => (
                  <div key={index} className="glass-card p-4 border-white/5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-white">{item.type}</span>
                      <span className="text-neon-cyan font-mono">{item.requirement}</span>
                    </div>
                    <p className="text-sm text-slate-500">{item.description}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-300">
                    You must carry valid insurance at all times. Medex Group Insurance supplements 
                    your existing coverage during active trips - it does not replace your base 
                    commercial auto and general liability policies.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Right - Onboarding */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-heading font-bold text-white mb-6">
                Onboarding <span className="text-neon-green">Fees</span>
              </h2>
              
              <div className="glass-card p-6 mb-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Clock className="w-6 h-6 text-neon-green" />
                  <div>
                    <h3 className="text-lg font-semibold text-white">~30 Day Onboarding</h3>
                    <p className="text-sm text-slate-400">Annual fee based on fleet size</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {onboardingTiers.map((tier, index) => (
                    <div 
                      key={index} 
                      className={`flex items-center justify-between p-4 rounded-lg ${
                        tier.popular 
                          ? 'bg-neon-green/10 border border-neon-green/30' 
                          : 'bg-white/5'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Truck className={`w-5 h-5 ${tier.popular ? 'text-neon-green' : 'text-slate-400'}`} />
                        <span className="text-white">{tier.range}</span>
                        {tier.popular && (
                          <span className="text-xs bg-neon-green/20 text-neon-green px-2 py-0.5 rounded-full">
                            POPULAR
                          </span>
                        )}
                      </div>
                      <span className={`font-heading font-bold ${tier.popular ? 'text-neon-green' : 'text-white'}`}>
                        {tier.fee}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="glass-card p-4 border-neon-purple/30">
                <p className="text-sm text-slate-300">
                  <span className="text-neon-purple font-semibold">25+ vehicles?</span> Contact our 
                  enterprise team for custom onboarding and dedicated account management.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Need for Enrollment */}
      <section className="py-16 bg-medex-surface/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              What We Need to Get You <span className="text-neon-green">Enrolled</span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              To qualify for $1 Million in coverage, have the following ready:
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {enrollmentRequirements.map((req, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="glass-card p-4 flex items-start space-x-3"
              >
                <CheckCircle2 className="w-5 h-5 text-neon-green flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-white">{req.item}</h4>
                  <p className="text-xs text-slate-500">{req.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              Why <span className="text-neon-purple">Medex</span> Insurance?
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              We're not just offering insurance - we're transforming the NEMT industry.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8"
            >
              <div className="w-14 h-14 rounded-2xl bg-neon-cyan/20 flex items-center justify-center mb-6">
                <Scale className="w-7 h-7 text-neon-cyan" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Tackling Disparities</h3>
              <p className="text-slate-400">
                Unequal access to transportation services has long plagued the NEMT sector. 
                Medex has tailored its insurance plan to bridge this gap - ensuring affordability 
                and accessibility for equitable NEMT services to all, regardless of socio-economic background.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8"
            >
              <div className="w-14 h-14 rounded-2xl bg-neon-green/20 flex items-center justify-center mb-6">
                <TrendingDown className="w-7 h-7 text-neon-green" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Redefining Affordability</h3>
              <p className="text-slate-400">
                High rates have been a major roadblock for NEMT providers. Medex dismantles this 
                barrier with transparent, affordable group rates. By optimizing costs without 
                compromising quality, we make proper insurance accessible to every provider.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8"
            >
              <div className="w-14 h-14 rounded-2xl bg-red-500/20 flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-red-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Combating Fraud, Waste & Abuse</h3>
              <p className="text-slate-400">
                Medex takes a proactive stance against FWA through rigorous monitoring, data analytics, 
                and stringent protocols. By fostering a culture of accountability, we ensure every 
                dollar spent contributes to genuine patient care.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8"
            >
              <div className="w-14 h-14 rounded-2xl bg-neon-purple/20 flex items-center justify-center mb-6">
                <Heart className="w-7 h-7 text-neon-purple" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">A Healthier Future</h3>
              <p className="text-slate-400">
                This isn't just a business venture - it's a commitment to building a healthier, 
                more inclusive future. By addressing disparities, reducing costs, and combating FWA, 
                Medex paves the way for an industry that prioritizes patient well-being.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-medex-surface/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              Ready to Become a <span className="text-neon-green">Founding Provider?</span>
            </h2>
            <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
              Join the first Pay-Per-Mile NEMT insurance network. 
              Reserve your Founding Provider position today.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/insurance/founding-provider"
                className="btn-primary inline-flex items-center space-x-2"
              >
                <span>Become a Founding Provider</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/insurance/founding-provider#calculator"
                className="btn-secondary inline-flex items-center space-x-2"
              >
                <span>Calculate Savings</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-heading font-bold text-white text-center mb-12">
            Common Questions
          </h2>
          
          <div className="space-y-4">
            {[
              {
                q: 'Does Medex insurance replace my existing coverage?',
                a: 'No. Medex Group Insurance supplements your existing commercial auto and general liability policies. You must maintain valid coverage at all times. Medex activates specifically during active trips when you have a contractual obligation to passengers.'
              },
              {
                q: 'What happens if I have an accident while waiting for a trip?',
                a: 'If you\'re logged into the app but haven\'t accepted a trip yet (Period 2), your own commercial insurance is primary. Medex coverage activates once you accept a trip and begins en route to pickup.'
              },
              {
                q: 'Why do I need 2 years of loss runs?',
                a: 'Loss runs help us assess risk and ensure we\'re building a responsible group pool. They show your claims history and help us offer the most competitive rates possible for the group.'
              },
              {
                q: 'Can I join if I\'m a new company?',
                a: 'Contact us directly to discuss your situation. While we typically require established operational history, we evaluate each application on its merits.'
              },
              {
                q: 'Is the onboarding fee annual?',
                a: 'Yes, the onboarding fee is an annual fee that covers administrative costs, compliance verification, and maintaining your coverage within the group plan.'
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-6"
              >
                <h3 className="text-lg font-semibold text-white mb-2">{faq.q}</h3>
                <p className="text-slate-400">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default InsuranceDetailsPage;
